import sum from 'lodash/sum';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { createSlice, Dispatch } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';
import { IProductState, ICheckoutCartItem } from '../../@types/product';

// ----------------------------------------------------------------------

const initialState: IProductState = {
    isLoading: false,
    error: null,
    products: [],
    product: null,
    checkout: {
        activeStep: 0,
        cart: [],
        subtotal: 0,
        total: 0,
        discount: 0,
        shipping: 0,
        billing: null,
        totalItems: 0,
    },
};

const slice = createSlice({
    name: 'product',
    initialState,
    reducers: {
        // START LOADING
        startLoading(state) {
            state.isLoading = true;
        },

        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },

        // GET PRODUCTS
        getProductsSuccess(state, action) {
            state.isLoading = false;
            state.products = action.payload;
        },

        // GET PRODUCT
        getProductSuccess(state, action) {
            state.isLoading = false;
            state.product = action.payload;
        },

        // CHECKOUT
        getCart(state, action) {
            const cart: ICheckoutCartItem[] = action.payload;

            const totalItems = sum(cart.map((product) => product.quantity));
            const subtotal = sum(cart.map((product) => product.price * product.quantity));
            state.checkout.cart = cart;
            state.checkout.discount = state.checkout.discount || 0;
            state.checkout.shipping = state.checkout.shipping || 0;
            state.checkout.billing = state.checkout.billing || null;
            state.checkout.subtotal = subtotal;
            state.checkout.total = subtotal - state.checkout.discount;
            state.checkout.totalItems = totalItems;
        },

        addToCart(state, action) {
            const newProduct = action.payload;
            const isEmptyCart = !state.checkout.cart.length;

            if (isEmptyCart) {
                state.checkout.cart = [...state.checkout.cart, newProduct];
            } else {
                state.checkout.cart = state.checkout.cart.map((product) => {
                    const isExisted = product.id === newProduct.id;

                    if (isExisted) {
                        return {
                            ...product,
                            colors: uniq([...product.colors, ...newProduct.colors]),
                            quantity: product.quantity + 1,
                        };
                    }

                    return product;
                });
            }
            state.checkout.cart = uniqBy([...state.checkout.cart, newProduct], 'id');
            state.checkout.totalItems = sum(state.checkout.cart.map((product) => product.quantity));
        },

        deleteCart(state, action) {
            const updateCart = state.checkout.cart.filter((product) => product.id !== action.payload);

            state.checkout.cart = updateCart;
        },

        resetCart(state) {
            state.checkout.cart = [];
            state.checkout.billing = null;
            state.checkout.activeStep = 0;
            state.checkout.total = 0;
            state.checkout.subtotal = 0;
            state.checkout.discount = 0;
            state.checkout.shipping = 0;
            state.checkout.totalItems = 0;
        },

        backStep(state) {
            state.checkout.activeStep -= 1;
        },

        nextStep(state) {
            state.checkout.activeStep += 1;
        },

        gotoStep(state, action) {
            const step = action.payload;
            state.checkout.activeStep = step;
        },

        increaseQuantity(state, action) {
            const productId = action.payload;

            const updateCart = state.checkout.cart.map((product) => {
                if (product.id === productId) {
                    return {
                        ...product,
                        quantity: product.quantity + 1,
                    };
                }
                return product;
            });

            state.checkout.cart = updateCart;
        },

        decreaseQuantity(state, action) {
            const productId = action.payload;
            const updateCart = state.checkout.cart.map((product) => {
                if (product.id === productId) {
                    return {
                        ...product,
                        quantity: product.quantity - 1,
                    };
                }
                return product;
            });

            state.checkout.cart = updateCart;
        },

        createBilling(state, action) {
            state.checkout.billing = action.payload;
        },

        applyDiscount(state, action) {
            const discount = action.payload;
            state.checkout.discount = discount;
            state.checkout.total = state.checkout.subtotal - discount;
        },

        applyShipping(state, action) {
            const shipping = action.payload;
            state.checkout.shipping = shipping;
            state.checkout.total = state.checkout.subtotal - state.checkout.discount + shipping;
        },
    },
});

// Reducer
export default slice.reducer;

// Actions
export const {
    getCart,
    addToCart,
    resetCart,
    gotoStep,
    backStep,
    nextStep,
    deleteCart,
    createBilling,
    applyShipping,
    applyDiscount,
    increaseQuantity,
    decreaseQuantity,
} = slice.actions;

// ----------------------------------------------------------------------

const products = [
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Nike Air Force 1 NDESTRUKT",
        "code": "38BEE270",
        "sku": "WW75K5210YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 16.19,
        "priceSale": 16.19,
        "totalRating": 2.5,
        "totalReview": 2661,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 1754,
                "reviewCount": 8143
            },
            {
                "name": "2 Star",
                "starCount": 196,
                "reviewCount": 5733
            },
            {
                "name": "3 Star",
                "starCount": 5753,
                "reviewCount": 5942
            },
            {
                "name": "4 Star",
                "starCount": 8185,
                "reviewCount": 158
            },
            {
                "name": "5 Star",
                "starCount": 1358,
                "reviewCount": 6472
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 8772,
                "postedAt": "2023-03-09T07:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 2785,
                "postedAt": "2023-03-08T06:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 6936,
                "postedAt": "2023-03-07T05:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 1740,
                "postedAt": "2023-03-06T04:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 7756,
                "postedAt": "2023-03-05T03:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 5203,
                "postedAt": "2023-03-04T02:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 4865,
                "postedAt": "2023-03-03T01:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 6999,
                "postedAt": "2023-03-02T00:08:45.299Z"
            }
        ],
        "status": "sale",
        "inventoryType": "low_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 66,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 121,
        "createdAt": "2023-03-09T07:08:45.299Z",
        "category": "Accessories",
        "gender": "Kids",
        "colors": [
            "#00AB55",
            "#000000"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Foundations Matte Flip Flop",
        "code": "38BEE271",
        "sku": "WW75K5211YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 35.71,
        "priceSale": null,
        "totalRating": 2,
        "totalReview": 8178,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 7951,
                "reviewCount": 2424
            },
            {
                "name": "2 Star",
                "starCount": 3709,
                "reviewCount": 9835
            },
            {
                "name": "3 Star",
                "starCount": 538,
                "reviewCount": 2507
            },
            {
                "name": "4 Star",
                "starCount": 9525,
                "reviewCount": 5370
            },
            {
                "name": "5 Star",
                "starCount": 2498,
                "reviewCount": 7876
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 2637,
                "postedAt": "2023-03-09T07:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 5966,
                "postedAt": "2023-03-08T06:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 7695,
                "postedAt": "2023-03-07T05:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 9479,
                "postedAt": "2023-03-06T04:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 4898,
                "postedAt": "2023-03-05T03:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 9712,
                "postedAt": "2023-03-04T02:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 6224,
                "postedAt": "2023-03-03T01:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 8774,
                "postedAt": "2023-03-02T00:08:45.299Z"
            }
        ],
        "status": "new",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 64,
        "createdAt": "2023-03-08T06:08:45.299Z",
        "category": "Apparel",
        "gender": "Kids",
        "colors": [
            "#000000",
            "#FFFFFF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Nike Air Zoom Pegasus 37 A.I.R. Chaz Bear",
        "code": "38BEE272",
        "sku": "WW75K5212YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 34.3,
        "priceSale": null,
        "totalRating": 4.9,
        "totalReview": 4239,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 6488,
                "reviewCount": 4728
            },
            {
                "name": "2 Star",
                "starCount": 9046,
                "reviewCount": 3030
            },
            {
                "name": "3 Star",
                "starCount": 1924,
                "reviewCount": 3518
            },
            {
                "name": "4 Star",
                "starCount": 6809,
                "reviewCount": 284
            },
            {
                "name": "5 Star",
                "starCount": 1641,
                "reviewCount": 771
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 4713,
                "postedAt": "2023-03-09T07:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 8716,
                "postedAt": "2023-03-08T06:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 4134,
                "postedAt": "2023-03-07T05:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 9903,
                "postedAt": "2023-03-06T04:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 8768,
                "postedAt": "2023-03-05T03:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 2161,
                "postedAt": "2023-03-04T02:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 7934,
                "postedAt": "2023-03-03T01:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 2218,
                "postedAt": "2023-03-02T00:08:45.299Z"
            }
        ],
        "status": "",
        "inventoryType": "in_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 873,
        "createdAt": "2023-03-07T05:08:45.299Z",
        "category": "Accessories",
        "gender": "Kids",
        "colors": [
            "#FFFFFF",
            "#FFC0CB"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Arizona Soft Footbed Sandal",
        "code": "38BEE273",
        "sku": "WW75K5213YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 93.1,
        "priceSale": 93.1,
        "totalRating": 2,
        "totalReview": 8469,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 5069,
                "reviewCount": 6843
            },
            {
                "name": "2 Star",
                "starCount": 3089,
                "reviewCount": 1063
            },
            {
                "name": "3 Star",
                "starCount": 169,
                "reviewCount": 7923
            },
            {
                "name": "4 Star",
                "starCount": 7814,
                "reviewCount": 680
            },
            {
                "name": "5 Star",
                "starCount": 7955,
                "reviewCount": 6641
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 896,
                "postedAt": "2023-03-09T07:08:45.299Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 676,
                "postedAt": "2023-03-08T06:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 9553,
                "postedAt": "2023-03-07T05:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 152,
                "postedAt": "2023-03-06T04:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 6585,
                "postedAt": "2023-03-05T03:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 5488,
                "postedAt": "2023-03-04T02:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 5777,
                "postedAt": "2023-03-03T01:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 6614,
                "postedAt": "2023-03-02T00:08:45.301Z"
            }
        ],
        "status": "sale",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 91,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 725,
        "createdAt": "2023-03-06T04:08:45.301Z",
        "category": "Accessories",
        "gender": "Women",
        "colors": [
            "#FFC0CB",
            "#FF4842",
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Boston Soft Footbed Sandal",
        "code": "38BEE274",
        "sku": "WW75K5214YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 55.47,
        "priceSale": null,
        "totalRating": 4,
        "totalReview": 1653,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 8109,
                "reviewCount": 904
            },
            {
                "name": "2 Star",
                "starCount": 9608,
                "reviewCount": 3065
            },
            {
                "name": "3 Star",
                "starCount": 8147,
                "reviewCount": 3655
            },
            {
                "name": "4 Star",
                "starCount": 1031,
                "reviewCount": 175
            },
            {
                "name": "5 Star",
                "starCount": 212,
                "reviewCount": 5185
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 1969,
                "postedAt": "2023-03-09T07:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 7441,
                "postedAt": "2023-03-08T06:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 2321,
                "postedAt": "2023-03-07T05:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 7983,
                "postedAt": "2023-03-06T04:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 1143,
                "postedAt": "2023-03-05T03:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 1381,
                "postedAt": "2023-03-04T02:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 5908,
                "postedAt": "2023-03-03T01:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 4965,
                "postedAt": "2023-03-02T00:08:45.301Z"
            }
        ],
        "status": "",
        "inventoryType": "low_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 706,
        "createdAt": "2023-03-05T03:08:45.301Z",
        "category": "Accessories",
        "gender": "Kids",
        "colors": [
            "#FF4842",
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Zoom Freak 2",
        "code": "38BEE275",
        "sku": "WW75K5215YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 89.09,
        "priceSale": null,
        "totalRating": 5,
        "totalReview": 3083,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 6738,
                "reviewCount": 1917
            },
            {
                "name": "2 Star",
                "starCount": 3273,
                "reviewCount": 505
            },
            {
                "name": "3 Star",
                "starCount": 3111,
                "reviewCount": 9081
            },
            {
                "name": "4 Star",
                "starCount": 7194,
                "reviewCount": 5150
            },
            {
                "name": "5 Star",
                "starCount": 5530,
                "reviewCount": 4885
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 7523,
                "postedAt": "2023-03-09T07:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 1934,
                "postedAt": "2023-03-08T06:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 8248,
                "postedAt": "2023-03-07T05:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 1461,
                "postedAt": "2023-03-06T04:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 8384,
                "postedAt": "2023-03-05T03:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 1692,
                "postedAt": "2023-03-04T02:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 8916,
                "postedAt": "2023-03-03T01:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 6067,
                "postedAt": "2023-03-02T00:08:45.301Z"
            }
        ],
        "status": "new",
        "inventoryType": "in_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 746,
        "createdAt": "2023-03-04T02:08:45.301Z",
        "category": "Apparel",
        "gender": "Kids",
        "colors": [
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Gazelle Vintage low-top sneakers",
        "code": "38BEE276",
        "sku": "WW75K5216YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 44.39,
        "priceSale": 44.39,
        "totalRating": 4.9,
        "totalReview": 7659,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 8606,
                "reviewCount": 370
            },
            {
                "name": "2 Star",
                "starCount": 2128,
                "reviewCount": 7200
            },
            {
                "name": "3 Star",
                "starCount": 4868,
                "reviewCount": 1556
            },
            {
                "name": "4 Star",
                "starCount": 3533,
                "reviewCount": 9129
            },
            {
                "name": "5 Star",
                "starCount": 2440,
                "reviewCount": 9419
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 8843,
                "postedAt": "2023-03-09T07:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 2925,
                "postedAt": "2023-03-08T06:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 4903,
                "postedAt": "2023-03-07T05:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 2747,
                "postedAt": "2023-03-06T04:08:45.301Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 7112,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 5844,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 3226,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 7438,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "sale",
        "inventoryType": "low_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 59,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 572,
        "createdAt": "2023-03-03T01:08:45.302Z",
        "category": "Accessories",
        "gender": "Women",
        "colors": [
            "#00AB55",
            "#000000"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Jordan Delta",
        "code": "38BEE277",
        "sku": "WW75K5217YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 26.92,
        "priceSale": null,
        "totalRating": 5,
        "totalReview": 667,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 8712,
                "reviewCount": 7911
            },
            {
                "name": "2 Star",
                "starCount": 9495,
                "reviewCount": 9127
            },
            {
                "name": "3 Star",
                "starCount": 7447,
                "reviewCount": 5339
            },
            {
                "name": "4 Star",
                "starCount": 2832,
                "reviewCount": 9202
            },
            {
                "name": "5 Star",
                "starCount": 3761,
                "reviewCount": 6471
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 9747,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 7061,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 7001,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 3183,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 8557,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 4057,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 6038,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 4884,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 80,
        "createdAt": "2023-03-02T00:08:45.302Z",
        "category": "Accessories",
        "gender": "Kids",
        "colors": [
            "#FF4842",
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b9",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_9.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Air Jordan XXXV PF",
        "code": "38BEE278",
        "sku": "WW75K5218YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 45.35,
        "priceSale": null,
        "totalRating": 3.7,
        "totalReview": 5616,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 784,
                "reviewCount": 3893
            },
            {
                "name": "2 Star",
                "starCount": 9905,
                "reviewCount": 3518
            },
            {
                "name": "3 Star",
                "starCount": 5279,
                "reviewCount": 8839
            },
            {
                "name": "4 Star",
                "starCount": 6585,
                "reviewCount": 827
            },
            {
                "name": "5 Star",
                "starCount": 2754,
                "reviewCount": 1341
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 2027,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 3176,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 361,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 4185,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 8682,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 9642,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 1821,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 3851,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 754,
        "createdAt": "2023-02-28T23:08:45.302Z",
        "category": "Apparel",
        "gender": "Kids",
        "colors": [
            "#FFFFFF",
            "#FFC0CB"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b10",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_10.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Rod Laver low-top sneakers",
        "code": "38BEE279",
        "sku": "WW75K5219YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 26.96,
        "priceSale": 26.96,
        "totalRating": 2.5,
        "totalReview": 7571,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 525,
                "reviewCount": 4788
            },
            {
                "name": "2 Star",
                "starCount": 6677,
                "reviewCount": 4949
            },
            {
                "name": "3 Star",
                "starCount": 2454,
                "reviewCount": 5982
            },
            {
                "name": "4 Star",
                "starCount": 993,
                "reviewCount": 7103
            },
            {
                "name": "5 Star",
                "starCount": 4127,
                "reviewCount": 2545
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 8571,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 849,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 3497,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 7313,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 8247,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 2588,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 642,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 1928,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "sale",
        "inventoryType": "in_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 65,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 33,
        "createdAt": "2023-02-27T22:08:45.302Z",
        "category": "Accessories",
        "gender": "Kids",
        "colors": [
            "#FFFFFF",
            "#FFC0CB",
            "#FF4842",
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b11",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_11.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Kyrie 7 EP Sisterhood",
        "code": "38BEE2710",
        "sku": "WW75K52110YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 78.22,
        "priceSale": null,
        "totalRating": 2,
        "totalReview": 3192,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 6752,
                "reviewCount": 9469
            },
            {
                "name": "2 Star",
                "starCount": 3265,
                "reviewCount": 1763
            },
            {
                "name": "3 Star",
                "starCount": 5710,
                "reviewCount": 9185
            },
            {
                "name": "4 Star",
                "starCount": 7945,
                "reviewCount": 252
            },
            {
                "name": "5 Star",
                "starCount": 1787,
                "reviewCount": 5412
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 2345,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 1505,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 9814,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 2947,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 6059,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 873,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 8267,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 5022,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "",
        "inventoryType": "low_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 94,
        "createdAt": "2023-02-26T21:08:45.302Z",
        "category": "Shose",
        "gender": "Men",
        "colors": [
            "#FFC0CB",
            "#FF4842",
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b12",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_12.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Pharrell Williams Human Race NMD sneakers",
        "code": "38BEE2711",
        "sku": "WW75K52111YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 35.54,
        "priceSale": null,
        "totalRating": 4.9,
        "totalReview": 2628,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 1938,
                "reviewCount": 2198
            },
            {
                "name": "2 Star",
                "starCount": 4326,
                "reviewCount": 546
            },
            {
                "name": "3 Star",
                "starCount": 7459,
                "reviewCount": 1696
            },
            {
                "name": "4 Star",
                "starCount": 9926,
                "reviewCount": 308
            },
            {
                "name": "5 Star",
                "starCount": 3443,
                "reviewCount": 2445
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 5928,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 7550,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 9302,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 7557,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 6139,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 9536,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 5888,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 971,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 773,
        "createdAt": "2023-02-25T20:08:45.302Z",
        "category": "Accessories",
        "gender": "Men",
        "colors": [
            "#FFFFFF",
            "#FFC0CB",
            "#FF4842",
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b13",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_13.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Nike Blazer Low 77 Vintage",
        "code": "38BEE2712",
        "sku": "WW75K52112YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 90.69,
        "priceSale": 90.69,
        "totalRating": 4.8,
        "totalReview": 8232,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 1132,
                "reviewCount": 7488
            },
            {
                "name": "2 Star",
                "starCount": 3065,
                "reviewCount": 9103
            },
            {
                "name": "3 Star",
                "starCount": 2582,
                "reviewCount": 3807
            },
            {
                "name": "4 Star",
                "starCount": 8275,
                "reviewCount": 7520
            },
            {
                "name": "5 Star",
                "starCount": 7417,
                "reviewCount": 4553
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 6083,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 6792,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 6610,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 4834,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 6835,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 5710,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 1933,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 8770,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "sale",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 44,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 146,
        "createdAt": "2023-02-24T19:08:45.302Z",
        "category": "Shose",
        "gender": "Kids",
        "colors": [
            "#FFFFFF",
            "#FFC0CB",
            "#FF4842",
            "#1890FF",
            "#94D82D"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b14",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_14.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "ASMC Winter Boot Cold.Rdy",
        "code": "38BEE2713",
        "sku": "WW75K52113YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 63.61,
        "priceSale": null,
        "totalRating": 4,
        "totalReview": 92,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 3142,
                "reviewCount": 2268
            },
            {
                "name": "2 Star",
                "starCount": 5609,
                "reviewCount": 1386
            },
            {
                "name": "3 Star",
                "starCount": 5680,
                "reviewCount": 2172
            },
            {
                "name": "4 Star",
                "starCount": 6237,
                "reviewCount": 430
            },
            {
                "name": "5 Star",
                "starCount": 1436,
                "reviewCount": 1069
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 6756,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 5770,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 7424,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 3396,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 2281,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 5650,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 3665,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 3747,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "",
        "inventoryType": "in_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 383,
        "createdAt": "2023-02-23T18:08:45.302Z",
        "category": "Shose",
        "gender": "Men",
        "colors": [
            "#FF4842",
            "#1890FF",
            "#94D82D"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b15",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_15.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "ZX 8000 Lego sneakers",
        "code": "38BEE2714",
        "sku": "WW75K52114YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 67.55,
        "priceSale": null,
        "totalRating": 2,
        "totalReview": 5443,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 5648,
                "reviewCount": 9769
            },
            {
                "name": "2 Star",
                "starCount": 2277,
                "reviewCount": 7996
            },
            {
                "name": "3 Star",
                "starCount": 3064,
                "reviewCount": 4458
            },
            {
                "name": "4 Star",
                "starCount": 931,
                "reviewCount": 7444
            },
            {
                "name": "5 Star",
                "starCount": 111,
                "reviewCount": 8460
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 4805,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 6680,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 1568,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 7109,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 694,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 6227,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 8540,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 1827,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "",
        "inventoryType": "low_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 365,
        "createdAt": "2023-02-22T17:08:45.302Z",
        "category": "Apparel",
        "gender": "Men",
        "colors": [
            "#00AB55",
            "#000000"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b16",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_16.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Ultraboost 21 sneakers",
        "code": "38BEE2715",
        "sku": "WW75K52115YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 94.75,
        "priceSale": 94.75,
        "totalRating": 3.7,
        "totalReview": 508,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 2474,
                "reviewCount": 4649
            },
            {
                "name": "2 Star",
                "starCount": 7213,
                "reviewCount": 523
            },
            {
                "name": "3 Star",
                "starCount": 5664,
                "reviewCount": 2121
            },
            {
                "name": "4 Star",
                "starCount": 5589,
                "reviewCount": 8538
            },
            {
                "name": "5 Star",
                "starCount": 7861,
                "reviewCount": 2854
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 703,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 8360,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 5557,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 3092,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 4384,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 4051,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 2720,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 3148,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "sale",
        "inventoryType": "low_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 81,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 183,
        "createdAt": "2023-02-21T16:08:45.302Z",
        "category": "Accessories",
        "gender": "Kids",
        "colors": [
            "#1890FF",
            "#94D82D",
            "#FFC107"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b17",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_17.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "2750 Cotu Classic Sneaker",
        "code": "38BEE2716",
        "sku": "WW75K52116YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 75.78,
        "priceSale": null,
        "totalRating": 1.4,
        "totalReview": 4318,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 1900,
                "reviewCount": 7857
            },
            {
                "name": "2 Star",
                "starCount": 7390,
                "reviewCount": 7200
            },
            {
                "name": "3 Star",
                "starCount": 711,
                "reviewCount": 1184
            },
            {
                "name": "4 Star",
                "starCount": 7030,
                "reviewCount": 2926
            },
            {
                "name": "5 Star",
                "starCount": 9628,
                "reviewCount": 5025
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 9909,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 9664,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 3588,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 3196,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 2183,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 7269,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 1722,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 7996,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "",
        "inventoryType": "low_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 791,
        "createdAt": "2023-02-20T15:08:45.302Z",
        "category": "Accessories",
        "gender": "Kids",
        "colors": [
            "#FF4842",
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b18",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_18.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "ZX 9000 A-ZX Series sneakers",
        "code": "38BEE2717",
        "sku": "WW75K52117YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 39.6,
        "priceSale": null,
        "totalRating": 2.4,
        "totalReview": 8409,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 5499,
                "reviewCount": 5683
            },
            {
                "name": "2 Star",
                "starCount": 1915,
                "reviewCount": 570
            },
            {
                "name": "3 Star",
                "starCount": 3757,
                "reviewCount": 4021
            },
            {
                "name": "4 Star",
                "starCount": 6054,
                "reviewCount": 2548
            },
            {
                "name": "5 Star",
                "starCount": 4635,
                "reviewCount": 5997
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 4394,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 449,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 186,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 686,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 1345,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 2059,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 3837,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 4079,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "",
        "inventoryType": "low_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 573,
        "createdAt": "2023-02-19T14:08:45.302Z",
        "category": "Accessories",
        "gender": "Women",
        "colors": [
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b19",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_19.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Madrid Big Buckle Sandal",
        "code": "38BEE2718",
        "sku": "WW75K52118YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 52.84,
        "priceSale": 52.84,
        "totalRating": 1.8,
        "totalReview": 3241,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 9454,
                "reviewCount": 3730
            },
            {
                "name": "2 Star",
                "starCount": 9854,
                "reviewCount": 2439
            },
            {
                "name": "3 Star",
                "starCount": 8966,
                "reviewCount": 6173
            },
            {
                "name": "4 Star",
                "starCount": 5921,
                "reviewCount": 1130
            },
            {
                "name": "5 Star",
                "starCount": 2860,
                "reviewCount": 3375
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 1717,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 5101,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 2611,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 5827,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 6213,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 5335,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 6006,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 133,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "sale",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 54,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 196,
        "createdAt": "2023-02-18T13:08:45.302Z",
        "category": "Accessories",
        "gender": "Women",
        "colors": [
            "#00AB55",
            "#000000"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b20",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_20.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Chuck 70 Hi Sneaker",
        "code": "38BEE2719",
        "sku": "WW75K52119YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 72.8,
        "priceSale": null,
        "totalRating": 5,
        "totalReview": 6341,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 8238,
                "reviewCount": 1075
            },
            {
                "name": "2 Star",
                "starCount": 3152,
                "reviewCount": 2941
            },
            {
                "name": "3 Star",
                "starCount": 9058,
                "reviewCount": 1250
            },
            {
                "name": "4 Star",
                "starCount": 6852,
                "reviewCount": 7215
            },
            {
                "name": "5 Star",
                "starCount": 3212,
                "reviewCount": 6445
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 4869,
                "postedAt": "2023-03-09T07:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 9331,
                "postedAt": "2023-03-08T06:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 7132,
                "postedAt": "2023-03-07T05:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 7820,
                "postedAt": "2023-03-06T04:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 1412,
                "postedAt": "2023-03-05T03:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 7515,
                "postedAt": "2023-03-04T02:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 8464,
                "postedAt": "2023-03-03T01:08:45.302Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 1677,
                "postedAt": "2023-03-02T00:08:45.302Z"
            }
        ],
        "status": "",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 968,
        "createdAt": "2023-02-17T12:08:45.302Z",
        "category": "Shose",
        "gender": "Women",
        "colors": [
            "#FF4842",
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b21",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_21.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Relaxed Adjustable Strap Slingback Sandal",
        "code": "38BEE2720",
        "sku": "WW75K52120YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 83.08,
        "priceSale": null,
        "totalRating": 2.9,
        "totalReview": 4366,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 5172,
                "reviewCount": 4238
            },
            {
                "name": "2 Star",
                "starCount": 6797,
                "reviewCount": 4090
            },
            {
                "name": "3 Star",
                "starCount": 9871,
                "reviewCount": 2061
            },
            {
                "name": "4 Star",
                "starCount": 212,
                "reviewCount": 1483
            },
            {
                "name": "5 Star",
                "starCount": 9282,
                "reviewCount": 6956
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 6171,
                "postedAt": "2023-03-09T07:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 3859,
                "postedAt": "2023-03-08T06:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 1423,
                "postedAt": "2023-03-07T05:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 3295,
                "postedAt": "2023-03-06T04:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 4060,
                "postedAt": "2023-03-05T03:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 5279,
                "postedAt": "2023-03-04T02:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 1263,
                "postedAt": "2023-03-03T01:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 2177,
                "postedAt": "2023-03-02T00:08:45.303Z"
            }
        ],
        "status": "",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 770,
        "createdAt": "2023-02-16T11:08:45.303Z",
        "category": "Accessories",
        "gender": "Women",
        "colors": [
            "#FF4842",
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b22",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_22.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Superturf Adventure X Atmos",
        "code": "38BEE2721",
        "sku": "WW75K52121YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 85.02,
        "priceSale": 85.02,
        "totalRating": 3.9,
        "totalReview": 1420,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 6665,
                "reviewCount": 1742
            },
            {
                "name": "2 Star",
                "starCount": 228,
                "reviewCount": 6753
            },
            {
                "name": "3 Star",
                "starCount": 9,
                "reviewCount": 1590
            },
            {
                "name": "4 Star",
                "starCount": 2548,
                "reviewCount": 1100
            },
            {
                "name": "5 Star",
                "starCount": 7947,
                "reviewCount": 9610
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 2970,
                "postedAt": "2023-03-09T07:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 9984,
                "postedAt": "2023-03-08T06:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 7319,
                "postedAt": "2023-03-07T05:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 9402,
                "postedAt": "2023-03-06T04:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 772,
                "postedAt": "2023-03-05T03:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 6478,
                "postedAt": "2023-03-04T02:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 665,
                "postedAt": "2023-03-03T01:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 5169,
                "postedAt": "2023-03-02T00:08:45.303Z"
            }
        ],
        "status": "sale",
        "inventoryType": "low_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 81,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 350,
        "createdAt": "2023-02-15T10:08:45.303Z",
        "category": "Shose",
        "gender": "Women",
        "colors": [
            "#1890FF"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b23",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_23.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Chuck Taylor All Star Lift Sneaker",
        "code": "38BEE2722",
        "sku": "WW75K52122YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 69.22,
        "priceSale": null,
        "totalRating": 3.9,
        "totalReview": 5386,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 7205,
                "reviewCount": 172
            },
            {
                "name": "2 Star",
                "starCount": 8726,
                "reviewCount": 3686
            },
            {
                "name": "3 Star",
                "starCount": 9227,
                "reviewCount": 2264
            },
            {
                "name": "4 Star",
                "starCount": 1797,
                "reviewCount": 1651
            },
            {
                "name": "5 Star",
                "starCount": 3808,
                "reviewCount": 9078
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 5370,
                "postedAt": "2023-03-09T07:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 2971,
                "postedAt": "2023-03-08T06:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 4713,
                "postedAt": "2023-03-07T05:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 2077,
                "postedAt": "2023-03-06T04:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 9487,
                "postedAt": "2023-03-05T03:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 6490,
                "postedAt": "2023-03-04T02:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 8616,
                "postedAt": "2023-03-03T01:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 3381,
                "postedAt": "2023-03-02T00:08:45.303Z"
            }
        ],
        "status": "",
        "inventoryType": "out_of_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 406,
        "createdAt": "2023-02-14T09:08:45.303Z",
        "category": "Shose",
        "gender": "Kids",
        "colors": [
            "#1890FF",
            "#94D82D",
            "#FFC107"
        ]
    },
    {
        "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b24",
        "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_24.jpg",
        "images": [
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
            "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
        ],
        "name": "Run Star Hike Platform Sneaker",
        "code": "38BEE2723",
        "sku": "WW75K52123YW/SV",
        "tags": [
            "Dangal",
            "The Sting",
            "2001: A Space Odyssey",
            "Singin' in the Rain"
        ],
        "price": 60.96,
        "priceSale": null,
        "totalRating": 1.8,
        "totalReview": 6184,
        "ratings": [
            {
                "name": "1 Star",
                "starCount": 2152,
                "reviewCount": 7988
            },
            {
                "name": "2 Star",
                "starCount": 6374,
                "reviewCount": 3425
            },
            {
                "name": "3 Star",
                "starCount": 5244,
                "reviewCount": 9586
            },
            {
                "name": "4 Star",
                "starCount": 6815,
                "reviewCount": 2314
            },
            {
                "name": "5 Star",
                "starCount": 7014,
                "reviewCount": 3672
            }
        ],
        "reviews": [
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
                "name": "Jayvion Simon",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
                "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
                "rating": 2.5,
                "isPurchased": true,
                "helpful": 5603,
                "postedAt": "2023-03-09T07:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
                "name": "Lucian Obrien",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
                "comment": "Quis veniam aut saepe aliquid nulla.",
                "rating": 2,
                "isPurchased": true,
                "helpful": 2433,
                "postedAt": "2023-03-08T06:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
                "name": "Deja Brady",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
                "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
                "rating": 4.9,
                "isPurchased": true,
                "helpful": 6675,
                "postedAt": "2023-03-07T05:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
                "name": "Harrison Stein",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
                "comment": "Error ut sit vel molestias velit.",
                "rating": 2,
                "isPurchased": false,
                "helpful": 8660,
                "postedAt": "2023-03-06T04:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
                "name": "Reece Chung",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
                "comment": "Quo quia sit nihil nemo doloremque et.",
                "rating": 4,
                "isPurchased": false,
                "helpful": 9754,
                "postedAt": "2023-03-05T03:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
                "name": "Lainey Davidson",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
                "comment": "Autem doloribus harum vero laborum.",
                "rating": 5,
                "isPurchased": true,
                "helpful": 3492,
                "postedAt": "2023-03-04T02:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
                "name": "Cristopher Cardenas",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
                "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
                "rating": 4.9,
                "isPurchased": false,
                "helpful": 3208,
                "postedAt": "2023-03-03T01:08:45.303Z"
            },
            {
                "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
                "name": "Melanie Noble",
                "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
                "comment": "Voluptas sunt magni adipisci praesentium saepe.",
                "rating": 5,
                "isPurchased": false,
                "helpful": 3700,
                "postedAt": "2023-03-02T00:08:45.303Z"
            }
        ],
        "status": "",
        "inventoryType": "in_stock",
        "sizes": [
            "6",
            "7",
            "8",
            "8.5",
            "9",
            "9.5",
            "10",
            "10.5",
            "11",
            "11.5",
            "12",
            "13"
        ],
        "available": 2,
        "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
        "sold": 604,
        "createdAt": "2023-02-13T08:08:45.303Z",
        "category": "Accessories",
        "gender": "Kids",
        "colors": [
            "#FF4842",
            "#1890FF"
        ]
    }
]

export function getProducts() {
    return async (dispatch: Dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            let response = products;
            dispatch(slice.actions.getProductsSuccess(response));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// ----------------------------------------------------------------------

const product = {
    "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b14",
    "cover": "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_14.jpg",
    "images": [
        "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_1.jpg",
        "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_2.jpg",
        "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_3.jpg",
        "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_4.jpg",
        "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_5.jpg",
        "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_6.jpg",
        "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_7.jpg",
        "https://api-prod-minimal-v4.vercel.app/assets/images/products/product_8.jpg"
    ],
    "name": "ASMC Winter Boot Cold.Rdy",
    "code": "38BEE2713",
    "sku": "WW75K52113YW/SV",
    "tags": [
        "Dangal",
        "The Sting",
        "2001: A Space Odyssey",
        "Singin' in the Rain"
    ],
    "price": 63.61,
    "priceSale": null,
    "totalRating": 4,
    "totalReview": 9580,
    "ratings": [
        {
            "name": "1 Star",
            "starCount": 8209,
            "reviewCount": 2432
        },
        {
            "name": "2 Star",
            "starCount": 4711,
            "reviewCount": 3242
        },
        {
            "name": "3 Star",
            "starCount": 8677,
            "reviewCount": 6152
        },
        {
            "name": "4 Star",
            "starCount": 3336,
            "reviewCount": 4952
        },
        {
            "name": "5 Star",
            "starCount": 9357,
            "reviewCount": 2887
        }
    ],
    "reviews": [
        {
            "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1",
            "name": "Jayvion Simon",
            "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_1.jpg",
            "comment": "Assumenda nam repudiandae rerum fugiat vel maxime.",
            "rating": 2.5,
            "isPurchased": true,
            "helpful": 8810,
            "postedAt": "2023-03-09T08:21:50.819Z"
        },
        {
            "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b2",
            "name": "Lucian Obrien",
            "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_2.jpg",
            "comment": "Quis veniam aut saepe aliquid nulla.",
            "rating": 2,
            "isPurchased": true,
            "helpful": 6353,
            "postedAt": "2023-03-08T07:21:50.819Z"
        },
        {
            "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b3",
            "name": "Deja Brady",
            "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_3.jpg",
            "comment": "Reprehenderit ut voluptas sapiente ratione nostrum est.",
            "rating": 4.9,
            "isPurchased": true,
            "helpful": 73,
            "postedAt": "2023-03-07T06:21:50.819Z"
        },
        {
            "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b4",
            "name": "Harrison Stein",
            "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_4.jpg",
            "comment": "Error ut sit vel molestias velit.",
            "rating": 2,
            "isPurchased": false,
            "helpful": 6627,
            "postedAt": "2023-03-06T05:21:50.819Z"
        },
        {
            "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5",
            "name": "Reece Chung",
            "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_5.jpg",
            "comment": "Quo quia sit nihil nemo doloremque et.",
            "rating": 4,
            "isPurchased": false,
            "helpful": 8701,
            "postedAt": "2023-03-05T04:21:50.819Z"
        },
        {
            "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b6",
            "name": "Lainey Davidson",
            "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_6.jpg",
            "comment": "Autem doloribus harum vero laborum.",
            "rating": 5,
            "isPurchased": true,
            "helpful": 4448,
            "postedAt": "2023-03-04T03:21:50.819Z"
        },
        {
            "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b7",
            "name": "Cristopher Cardenas",
            "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_7.jpg",
            "comment": "Tempora officiis consequuntur architecto nostrum autem nam adipisci.",
            "rating": 4.9,
            "isPurchased": false,
            "helpful": 4880,
            "postedAt": "2023-03-03T02:21:50.819Z"
        },
        {
            "id": "e99f09a7-dd88-49d5-b1c8-1daf80c2d7b8",
            "name": "Melanie Noble",
            "avatarUrl": "https://api-prod-minimal-v4.vercel.app/assets/images/avatars/avatar_8.jpg",
            "comment": "Voluptas sunt magni adipisci praesentium saepe.",
            "rating": 5,
            "isPurchased": false,
            "helpful": 5300,
            "postedAt": "2023-03-02T01:21:50.819Z"
        }
    ],
    "status": "",
    "inventoryType": "low_stock",
    "sizes": [
        "6",
        "7",
        "8",
        "8.5",
        "9",
        "9.5",
        "10",
        "10.5",
        "11",
        "11.5",
        "12",
        "13"
    ],
    "available": 2,
    "description": "\n<p><strong><small> SPECIFICATION</small></strong></p>\n<p>Leather panels. Laces. Rounded toe. Rubber sole.\n<br /><br />\n<p><strong><small> MATERIAL AND WASHING INSTRUCTIONS</small></strong></p>\n<p>Shoeupper: 54% bovine leather,46% polyurethane. Lining: 65% polyester,35% cotton. Insole: 100% polyurethane. Sole: 100% thermoplastic. Fixing sole: 100% glued.</p>\n",
    "sold": 992,
    "createdAt": "2023-02-23T19:21:50.819Z",
    "category": "Accessories",
    "gender": "Kids",
    "colors": [
        "#FF4842",
        "#1890FF",
        "#94D82D"
    ]
};

export function getProduct(name: string) {
    return async (dispatch: Dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            let response = product;
            dispatch(slice.actions.getProductSuccess(response));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}
